import React, { useEffect, useState } from 'react';
import { XMarkIcon, DocumentIcon, CodeBracketIcon, DocumentTextIcon, PhotoIcon } from "@heroicons/react/24/outline";

const FileContextDisplay = ({ file, context, onRemove, dataUrl, showRemoveButtonAlways = false }) => {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  
  // Check if the file is an image
  const isImage = file?.type && file.type.startsWith('image/');
  const isHeicLikeImage = /image\/hei(c|f)/i.test(file?.type || '') || /\.(heic|heif)$/i.test(file?.name || '');

  useEffect(() => {
    setImageLoadFailed(false);
  }, [file?.name, dataUrl]);

  if (!file) return null;
  
  if (isImage && !isHeicLikeImage && !dataUrl) {
    // Image loading - show skeleton matching final image dimensions
    return (
      <div className="relative inline-block">
        <div className="w-20 h-20 chat-input-file-preview-squircle bg-gray-200 dark:bg-[#3d3d3d] animate-pulse" />
        <div 
          onClick={onRemove}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onRemove();
            }
          }}
          className="absolute top-0.5 right-0.5 p-0.5 bg-black hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-200 rounded-full text-white dark:text-black flex-shrink-0 shadow-sm cursor-pointer opacity-70"
        >
          <XMarkIcon className="w-3 h-3" strokeWidth={2.5} />
        </div>
      </div>
    );
  }
  
  if (isImage && !isHeicLikeImage && dataUrl && !imageLoadFailed) {
    // For images, show just the image with X in top right
    return (
      <div className="relative inline-block group">
        <img 
          src={dataUrl} 
          alt={file.name}
          className="w-20 h-20 object-cover chat-input-file-preview-squircle border border-gray-200 dark:border-[#282828] opacity-0"
          style={{ animation: 'fade-in 0.15s ease-out forwards' }}
          onError={() => {
            // Fall back to file chip for unsupported browser image codecs.
            setImageLoadFailed(true);
          }}
        />
        <div 
          onClick={onRemove}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onRemove();
            }
          }}
          className={`absolute top-1.5 right-1.5 p-0.5 bg-black hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-200 rounded-full text-white dark:text-black flex-shrink-0 shadow-sm transition-opacity duration-200 cursor-pointer ${
            showRemoveButtonAlways 
              ? 'opacity-100' 
              : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <XMarkIcon className="w-3 h-3" strokeWidth={2.5} />
        </div>
      </div>
    );
  }

  if (isImage && (isHeicLikeImage || imageLoadFailed)) {
    // Browser can't decode this image format (or decoding failed), so keep
    // the same square preview footprint with a placeholder thumbnail tile.
    return (
      <div className="relative inline-block group">
        <div className="w-20 h-20 chat-input-file-preview-squircle border border-gray-200 dark:border-[#282828] bg-gray-100 dark:bg-[#2a2a2a] flex flex-col items-center justify-center px-1">
          <PhotoIcon className="w-5 h-5 text-gray-500 dark:text-gray-300 mb-1" />
          <div className="text-[9px] leading-tight text-center text-gray-600 dark:text-gray-300 break-words line-clamp-2">
            {file.name}
          </div>
        </div>
        <div
          onClick={onRemove}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onRemove();
            }
          }}
          className={`absolute top-1.5 right-1.5 p-0.5 bg-black hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-200 rounded-full text-white dark:text-black flex-shrink-0 shadow-sm transition-opacity duration-200 cursor-pointer ${
            showRemoveButtonAlways
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <XMarkIcon className="w-3 h-3" strokeWidth={2.5} />
        </div>
      </div>
    );
  }
  
  // For non-image files, show the original layout with colored icons
  const fileName = file.name.toLowerCase();
  
  // Define file categories (same logic as ChatMessage.js)
  const isPDF = fileName.endsWith('.pdf');
  const isCodeFile = fileName.endsWith('.js') || 
                   fileName.endsWith('.jsx') || 
                   fileName.endsWith('.ts') || 
                   fileName.endsWith('.tsx') || 
                   fileName.endsWith('.py') || 
                   fileName.endsWith('.java') || 
                   fileName.endsWith('.cpp') || 
                   fileName.endsWith('.c') || 
                   fileName.endsWith('.cs') || 
                   fileName.endsWith('.php') || 
                   fileName.endsWith('.rb') || 
                   fileName.endsWith('.go') || 
                   fileName.endsWith('.rs') || 
                   fileName.endsWith('.swift') || 
                   fileName.endsWith('.kt') || 
                   fileName.endsWith('.scala') || 
                   fileName.endsWith('.r') || 
                   fileName.endsWith('.m') || 
                   fileName.endsWith('.html') || 
                   fileName.endsWith('.css') || 
                   fileName.endsWith('.scss') || 
                   fileName.endsWith('.sass') || 
                   fileName.endsWith('.less') || 
                   fileName.endsWith('.json') || 
                   fileName.endsWith('.xml') || 
                   fileName.endsWith('.yaml') || 
                   fileName.endsWith('.yml') || 
                   fileName.endsWith('.sql') || 
                   fileName.endsWith('.sh') || 
                   fileName.endsWith('.bat') || 
                   fileName.endsWith('.ps1');
  const isWordDoc = fileName.endsWith('.doc') || 
                  fileName.endsWith('.docx') || 
                  fileName.endsWith('.rtf') || 
                  fileName.endsWith('.odt');
  const isOtherDoc = fileName.endsWith('.xls') || 
                   fileName.endsWith('.xlsx') || 
                   fileName.endsWith('.ppt') || 
                   fileName.endsWith('.pptx') || 
                   fileName.endsWith('.txt') || 
                   fileName.endsWith('.csv') || 
                   fileName.endsWith('.ods') || 
                   fileName.endsWith('.odp');
  
  // Determine icon and color
  let bgColor, IconComponent;
  if (isPDF) {
    bgColor = 'bg-orange-500';
    IconComponent = DocumentIcon;
  } else if (isCodeFile) {
    bgColor = 'bg-pink-500';
    IconComponent = CodeBracketIcon;
  } else if (isWordDoc) {
    bgColor = 'bg-blue-500';
    IconComponent = DocumentTextIcon;
  } else if (isOtherDoc) {
    bgColor = 'bg-orange-500';
    IconComponent = DocumentIcon;
  } else {
    bgColor = 'bg-gray-500';
    IconComponent = DocumentIcon;
  }

  return (
    <div className="group relative inline-flex items-center gap-3 pl-2 pr-6 py-1.5 bg-[#e5e5e5] dark:bg-[#2a2a2a] chat-input-file-chip-squircle max-w-full">
      <div className={`w-7 h-7 ${bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
        <IconComponent className="w-4 h-4 text-white" />
      </div>
      <div className="overflow-hidden">
        <div className="font-medium text-sm truncate max-w-[250px]">{file.name}</div>
        {context && <div className="text-xs text-gray-600 dark:text-gray-300 max-w-[250px] truncate">{context}</div>}
      </div>
      <div 
        onClick={onRemove}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRemove();
          }
        }}
        className="absolute top-1.5 right-1.5 p-0.5 bg-black hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-200 rounded-full text-white dark:text-black flex-shrink-0 shadow-sm cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      >
        <XMarkIcon className="w-3 h-3" strokeWidth={2.5} />
      </div>
    </div>
  );
};

export default FileContextDisplay; 